import axios, { AxiosProgressEvent } from 'axios';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';

import { LocalStore } from 'constants/localstore';
import { MAX_CONCURRENT_MEDIA_UPLOAD_PREPARE, MAX_CONCURRENT_MEDIA_UPLOADS } from 'constants/params';
import { FileProgressData, FileQueueData } from 'services/sse.service';
import { downloadFile, uploadFileService } from 'services/file-flow.service';
import { GoogleMediaItem } from 'services/google/photos.service';
import { MAX_TOKEN_TIME } from 'services/google/google.params';
import { shareFile } from 'services/google/youtube.service';
import {
  classifyDownloadError,
  classifyYoutubeApiError,
  isYtUploadError,
  ytAuthError,
} from 'services/google/youtube-upload.error';
import {
  anyProcessingFiles as anyProcessingFilesService,
  uploadFile,
  refreshUploadUrl,
  FileStatus,
  FileType,
  ProjectFile,
  changeStatus,
} from 'services/file.service';
import { LogLevel } from 'services/logger.service';
import type { AppDispatch, RootState } from 'store/store';
import { createSafeAsyncThunk } from 'store/store.helper';
import { isFilesProcessing, isFileStatusCompleted } from 'components/app/_helpers/_app.helper';
import { Error } from 'components/app/_helpers/app-errors.helper';
import { fetchFiles, putFile, removeFile, updateFileStatus } from './app-files.slice';
import {
  fetchStorageSize,
  fetchProjects,
  updateProjectStatusToUploading,
  updateProjectStatusToProcessing,
} from './app-data.slice';
import { INIT_THUMBNAIL, updateClipThumbnails } from 'components/app/_mediator/clip-thumb/clip-thumb.slice';
import { fetchClips } from 'components/app/editor/_mediator/editor.slice';
import { appLogger } from './app-logger.slice';

const SLICE_ID = 'app__filesUpload';

export type FileUplId = string; // `${projectId}__${fileName}`

export interface UploadingFile {
  id: FileUplId;
  projectId: ProjectId;
  fileName: string;
  progress: number;
  type: FileType;
}

interface UploadFiles {
  files: File[];
  reUpload: boolean;
}

interface UploadGFiles {
  files: GoogleMediaItem[];
  reUpload: boolean;
}

interface UploadYtFile {
  fcId: FinalcutId;
  metadata: string;
  url: Url;
}

interface DownloadFinalCut {
  fcId: FinalcutId;
  url: Url;
  fileName: string;
}

interface RetryInterruptedUploads {
  ids?: FileUplId[];
}

interface State {
  uploadingFiles: UploadingFile[];
  /** FileUplIds with a same-session File handle available for Retry. */
  retryableUploadIds: Record<FileUplId, true>;
  queueFiles: Record<FileId, number>;
  processingFiles: Record<FileId, number>;
  youtubeFiles: Record<FileId, number>;
  finalcutDownloads: Record<FileId, number>;
}

const initialState: State = {
  queueFiles: {},
  uploadingFiles: [],
  retryableUploadIds: {},
  processingFiles: {},
  youtubeFiles: {},
  finalcutDownloads: {},
};

const uploadFileControllers: Record<FileUplId, AbortController> = {};
/** MediaFile ids created during prepare — used so cancel can remove the DB row immediately. */
const uploadMediaFileIds: Record<FileUplId, FileId> = {};

/** Same-session File handles for interrupted uploads (not persisted). */
interface HeldInterruptedUpload {
  file: File;
  mediaFile?: ProjectFile;
  type: FileType;
  fileName: string;
  projectId: ProjectId;
}
const heldInterruptedUploads: Record<FileUplId, HeldInterruptedUpload> = {};

const UPLOAD_INTERRUPTED_TOAST_ID = 'media-upload-interrupted';
let uploadInterruptedToastCount = 0;

const resetUploadInterruptedToast = () => {
  uploadInterruptedToastCount = 0;
};

/** API rejections already carry a message (locked story, duplicate, etc.). Network drops do not. */
const notifyUploadFailure = (fileName: string, err: unknown) => {
  if (axios.isAxiosError(err) && err.response) {
    Error.reduxErrorHandler(err);
    return;
  }
  notifyUploadInterrupted(fileName);
};

const notifyUploadInterrupted = (fileName: string) => {
  uploadInterruptedToastCount += 1;
  const count = uploadInterruptedToastCount;
  const message =
    count === 1
      ? `Upload interrupted: ${fileName}. Use Retry, or re-select the file.`
      : `${count} uploads interrupted. Use Retry failed, or re-select files.`;

  // v9 drops a second toast.error with the same toastId while active — must update.
  if (toast.isActive(UPLOAD_INTERRUPTED_TOAST_ID)) {
    toast.update(UPLOAD_INTERRUPTED_TOAST_ID, { render: message, autoClose: 8000 });
  } else {
    toast.error(message, { toastId: UPLOAD_INTERRUPTED_TOAST_ID, autoClose: 8000 });
  }
};

const mapPool = async <T>(items: T[], concurrency: number, fn: (item: T) => Promise<void>) => {
  if (!items.length) return;
  let next = 0;
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (next < items.length) {
      const index = next++;
      await fn(items[index]);
    }
  });
  await Promise.all(workers);
};

const isUploadCanceled = (err: unknown) => axios.isAxiosError(err) && err.code === 'ERR_CANCELED';

export const makeFileUplId = (projectId: ProjectId, fileName: string): FileUplId => `${projectId}__${fileName}`;

const holdInterruptedUpload = (id: FileUplId, held: HeldInterruptedUpload, dispatch: AppDispatch) => {
  heldInterruptedUploads[id] = held;
  dispatch(slice.actions.markRetryable(id));
};

const clearHeldUpload = (id: FileUplId, dispatch: AppDispatch) => {
  delete heldInterruptedUploads[id];
  dispatch(slice.actions.clearRetryable(id));
};

/** Clear same-session retry handle when the media row is deleted. */
export const clearHeldByMediaFileId = (mediaFileId: FileId, dispatch: AppDispatch) => {
  for (const [id, held] of Object.entries(heldInterruptedUploads)) {
    if (held.mediaFile?.id === mediaFileId) clearHeldUpload(id, dispatch);
  }
};

const clearUploadingRow = (id: FileUplId, dispatch: AppDispatch, options?: { removeMedia?: boolean }) => {
  delete uploadFileControllers[id];
  const mediaFileId = uploadMediaFileIds[id] ?? heldInterruptedUploads[id]?.mediaFile?.id;
  delete uploadMediaFileIds[id];
  clearHeldUpload(id, dispatch);
  dispatch(slice.actions.uploadingFileEnd(id));
  if (options?.removeMedia && mediaFileId != null) dispatch(removeFile(mediaFileId));
};

interface PreparedUpload {
  file: File;
  mediaFile: ProjectFile;
  id: FileUplId;
  type: FileType;
  fileName: string;
  controller: AbortController;
}

type UploadThunkApi = { dispatch: AppDispatch; getState: () => unknown };

const runPutUploads = async (
  prepared: PreparedUpload[],
  projectId: ProjectId,
  { dispatch, getState }: UploadThunkApi
) => {
  if (!prepared.length) return;
  dispatch(updateProjectStatusToUploading(projectId));

  await mapPool(prepared, MAX_CONCURRENT_MEDIA_UPLOADS, async ({ file, mediaFile, id, type, fileName, controller }) => {
    if (controller.signal.aborted) {
      clearUploadingRow(id, dispatch, { removeMedia: true });
      return;
    }

    const signal = controller.signal;
    const setProgress = (progress: number) => {
      dispatch(slice.actions.uploadingFileProgress({ id, projectId, fileName, progress, type }));
    };
    const onUploadProgress = (payload: AxiosProgressEvent) => {
      const progress = Math.floor((payload.loaded * 100) / payload.total!);
      setProgress(progress);
    };

    try {
      // Fresh PUT URL for this PendingUpload row (avoids UploadFile remint → File is processing).
      const { data: signed } = await refreshUploadUrl(mediaFile.id, file.size);
      if (signal.aborted) {
        clearUploadingRow(id, dispatch, { removeMedia: true });
        return;
      }
      await uploadFileService({
        url: signed.url,
        file,
        onUploadProgress,
        signal,
        fileSize: file.size,
      });
      delete uploadFileControllers[id];
      delete uploadMediaFileIds[id];
      clearHeldUpload(id, dispatch);
      const { data } = await changeStatus(mediaFile.id);
      if ((getState() as RootState).projects.selectedProject === projectId) {
        await dispatch(putFile({ file: data, projectId }));
      }
      dispatch(slice.actions.uploadingFileEnd(id));
    } catch (err) {
      if (isUploadCanceled(err)) {
        clearUploadingRow(id, dispatch, { removeMedia: true });
        return;
      }
      delete uploadFileControllers[id];
      delete uploadMediaFileIds[id];
      const interruptedFile = { ...mediaFile, status: FileStatus.UploadInterrupted };
      holdInterruptedUpload(id, { file, mediaFile: interruptedFile, type, fileName, projectId }, dispatch);
      if ((getState() as RootState).projects.selectedProject === projectId) {
        await dispatch(putFile({ file: interruptedFile, projectId }));
      }
      dispatch(slice.actions.uploadingFileEnd(id));
      dispatch(
        appLogger({
          level: LogLevel.Error,
          error: {
            name: 'mediaUploadInterrupted',
            message: JSON.stringify({
              fileName,
              mediaFileId: mediaFile.id,
              projectId,
              phase: 'put',
              error: serializeError(err),
            }),
          },
        })
      );
      notifyUploadFailure(fileName, err);
    }
  });
};

// T H U N K S

export const uploadFiles = createSafeAsyncThunk(
  SLICE_ID + '/uploadFiles',
  async (params: UploadFiles, { dispatch, getState }) => {
    const { selectedProject: projectId } = (getState() as RootState).projects;
    if (!projectId) return;
    const reUpload = params.reUpload;
    resetUploadInterruptedToast();

    // Preserve selection order for the PUT pool (prepare workers finish out of order).
    const preparedSlots: (PreparedUpload | undefined)[] = new Array(params.files.length);

    // Create DB rows up front (bounded) so PendingUpload survives client interruption.
    // Per-file settle: one create failure must not block the rest of the batch.
    await mapPool(
      params.files.map((file, index) => ({ file, index })),
      MAX_CONCURRENT_MEDIA_UPLOAD_PREPARE,
      async ({ file, index }) => {
        const fileName = file.name;
        const type = getFileType(file);
        const id = makeFileUplId(projectId, fileName);
        clearHeldUpload(id, dispatch);
        const controller = new AbortController();
        uploadFileControllers[id] = controller;
        dispatch(slice.actions.uploadingFileProgress({ id, projectId, fileName, progress: 0, type }));

        try {
          // Create is intentionally not passed `signal` — cancel mid-create still gets a mediaFileId
          // so we can removeMedia after the request returns (see aborted check below).
          const { data } = await uploadFile({ projectId, key: fileName, reUpload }, file.size);
          uploadMediaFileIds[id] = data.file.id;
          if (controller.signal.aborted) {
            clearUploadingRow(id, dispatch, { removeMedia: true });
            return;
          }
          if ((getState() as RootState).projects.selectedProject === projectId) {
            await dispatch(putFile({ file: data.file, projectId }));
          }
          preparedSlots[index] = { file, mediaFile: data.file, id, type, fileName, controller };
        } catch (err) {
          const canceled = controller.signal.aborted || isUploadCanceled(err);
          if (canceled) {
            clearUploadingRow(id, dispatch, { removeMedia: true });
            return;
          }
          delete uploadFileControllers[id];
          delete uploadMediaFileIds[id];
          holdInterruptedUpload(id, { file, type, fileName, projectId }, dispatch);
          dispatch(slice.actions.uploadingFileEnd(id));
          dispatch(
            appLogger({
              level: LogLevel.Error,
              error: {
                name: 'mediaUploadInterrupted',
                message: JSON.stringify({
                  fileName,
                  projectId,
                  phase: 'prepare',
                  error: serializeError(err),
                }),
              },
            })
          );
          notifyUploadFailure(fileName, err);
        }
      }
    );

    const prepared = preparedSlots.filter((item): item is PreparedUpload => !!item);
    await runPutUploads(prepared, projectId, { dispatch, getState });
  }
);

export const retryInterruptedUploads = createSafeAsyncThunk(
  SLICE_ID + '/retryInterruptedUploads',
  async (params: RetryInterruptedUploads | undefined, { dispatch, getState }) => {
    const { selectedProject: projectId } = (getState() as RootState).projects;
    if (!projectId) return;

    const prefix = `${projectId}__`;
    const requestedIds =
      params?.ids ??
      Object.keys(heldInterruptedUploads).filter(
        (id) => id.startsWith(prefix) && heldInterruptedUploads[id]?.projectId === projectId
      );

    const prepareFiles: File[] = [];
    const putPrepared: PreparedUpload[] = [];
    let missingHandleCount = 0;

    for (const id of requestedIds) {
      const held = heldInterruptedUploads[id];
      if (!held || held.projectId !== projectId) {
        missingHandleCount += 1;
        continue;
      }
      if (uploadFileControllers[id]) continue;

      if (!held.mediaFile) {
        prepareFiles.push(held.file);
        clearHeldUpload(id, dispatch);
        continue;
      }

      const controller = new AbortController();
      uploadFileControllers[id] = controller;
      uploadMediaFileIds[id] = held.mediaFile.id;
      dispatch(
        slice.actions.uploadingFileProgress({
          id,
          projectId,
          fileName: held.fileName,
          progress: 0,
          type: held.type,
        })
      );
      // Drop retryable flag while in flight; failure path re-holds with refreshed mediaFile.
      dispatch(slice.actions.clearRetryable(id));
      putPrepared.push({
        file: held.file,
        mediaFile: held.mediaFile,
        id,
        type: held.type,
        fileName: held.fileName,
        controller,
      });
    }

    if (missingHandleCount) {
      toast.info(
        missingHandleCount === 1
          ? 'File handle unavailable — re-select the file to retry.'
          : `${missingHandleCount} file handles unavailable — re-select those files to retry.`
      );
    }

    if (prepareFiles.length) {
      await dispatch(uploadFiles({ files: prepareFiles, reUpload: true }));
    }
    if (putPrepared.length) {
      resetUploadInterruptedToast();
      await runPutUploads(putPrepared, projectId, { dispatch, getState });
    }
  }
);

export const uploadGFiles = createSafeAsyncThunk(
  SLICE_ID + '/uploadGFiles',
  async (params: UploadGFiles, { dispatch, getState }) => {
    const projectId = (getState() as RootState).projects.selectedProject;
    const token = localStorage.getItem(LocalStore.googleOAuth);
    if (!projectId || !token) return;
    dispatch(updateProjectStatusToUploading(projectId));
    await Promise.all(
      params.files.map(async (file) => {
        const url = file.type === 'VIDEO' ? file.mediaFile.baseUrl + '=dv' : file.mediaFile.baseUrl + '=d';
        const key = file.mediaFile.filename;
        await uploadFile(
          {
            key,
            projectId,
            reUpload: params.reUpload,
            google: { url, token },
          },
          0
        );
      })
    );
    await dispatch(fetchFiles(projectId));
  }
);

export const uploadYtFile = createSafeAsyncThunk(
  SLICE_ID + '/uploadToYoutube',
  async ({ fcId, url, metadata }: UploadYtFile, { rejectWithValue, dispatch }) => {
    const token = localStorage.getItem(LocalStore.youtubeOAuth);
    const tokenDate = localStorage.getItem(LocalStore.youtubeOAuthDate);

    if (!token || !tokenDate) return rejectWithValue(ytAuthError('missing'));

    if (Date.now() - Number(tokenDate) > MAX_TOKEN_TIME) {
      localStorage.removeItem(LocalStore.youtubeOAuth);
      localStorage.removeItem(LocalStore.youtubeOAuthDate);
      return rejectWithValue(ytAuthError('expired'));
    }

    toast.info('Upload in progress. Feel free to browse Pulse; you’ll get a notification once it’s finished.');
    const fileName = decodeURI(new URL(url).pathname.split('/').pop()!);
    const setProgress = (progress: number) => dispatch(slice.actions.setYoutubeProgress({ fcId, progress }));
    const dwnCb = (e: number) => setProgress((e * 100) / 2);
    const uplCb = (e: number) => setProgress((e * 100) / 2 + 50);

    try {
      let response;
      try {
        response = await downloadFile(url, dwnCb);
      } catch (err) {
        return rejectWithValue(classifyDownloadError(err));
      }

      const file = new File([response.data], fileName);
      try {
        await shareFile(file, metadata, uplCb);
      } catch (err) {
        return rejectWithValue(isYtUploadError(err) ? err : classifyYoutubeApiError(err));
      }

      toast.success('Video successfully uploaded to YouTube! 🎉');
    } finally {
      dispatch(slice.actions.setYoutubeProgress({ fcId, progress: null }));
    }
  }
);

export const removeUploadingFile = createSafeAsyncThunk(
  SLICE_ID + '/removeUploadingFile',
  async (fileId: FileUplId, { dispatch }) => {
    if (!uploadFileControllers[fileId] && !heldInterruptedUploads[fileId]) return;
    uploadFileControllers[fileId]?.abort();
    clearUploadingRow(fileId, dispatch, { removeMedia: true });
  }
);

export const handleProcessingFiles = createSafeAsyncThunk(
  SLICE_ID + '/handleProcessingFiles',
  async (payload: FileProgressData, { getState, dispatch }) => {
    const { fileId, status, progress, projectId } = payload;
    const state = getState() as RootState;
    const currStatus = state.app__files.statuses[fileId];
    const isSelectedProject = !!currStatus;

    if (status === FileStatus.Processing) {
      dispatch(slice.actions.clearQueuePosition({ fileId }));
    }
    if (progress) {
      dispatch(slice.actions.processingFileProgress({ fileId, progress: Math.floor(progress) }));
    }
    if (isSelectedProject) {
      if (currStatus === status) return;
      dispatch(updateFileStatus({ fileId, status }));
    }
    if (isFileStatusCompleted(status)) {
      dispatch(fetchProjects());
      dispatch(fetchStorageSize());
      if (isSelectedProject) dispatch(fetchFiles(state.projects.selectedProject!));

      // Fetch highlights when a new video or image file becomes available
      const selectedStory = state.projects.selectedStory;
      const file = state.app__files.files[fileId];
      if (selectedStory && file && [FileType.video, FileType.image].includes(file.fileType)) {
        // Only fetches clips for this specific file from backend
        dispatch(fetchClips({ storyId: selectedStory, fileId }));
      }

      // Clear thumbnails for clips that reference this file so Highlights regenerates them lazily
      const selector = (getState() as RootState).editor.selector;
      const clipIds = selector?.[fileId];
      if (clipIds && clipIds.length) {
        const initMap = clipIds.reduce(
          (acc, cid) => {
            acc[cid] = INIT_THUMBNAIL;
            return acc;
          },
          {} as Record<ClipId, typeof INIT_THUMBNAIL>
        );
        dispatch(updateClipThumbnails(initMap));
      }

      if (
        (isSelectedProject ? !isFilesProcessing((getState() as RootState).app__files.statuses) : true) &&
        !(await dispatch(anyProcessingFiles()).unwrap())
      )
        return { closeSse: true };
    } else dispatch(updateProjectStatusToProcessing(projectId));
  }
);

export const anyProcessingFiles = createSafeAsyncThunk(SLICE_ID + '/anyProcessingFiles', async () => {
  const res = await anyProcessingFilesService();
  return res.data;
});

export const downloadFinalcut = createSafeAsyncThunk(
  SLICE_ID + '/downloadFinalCut',
  async ({ fcId, url, fileName }: DownloadFinalCut, { dispatch }) => {
    const response = await downloadFile(url, (val: number) =>
      dispatch(slice.actions.setFinalcutProgress({ fcId, progress: val * 100 }))
    );
    const safeFileName = fileName.replaceAll('.', '_');
    const file = new File([response.data], safeFileName, { type: response.data.type });
    const blobUrl = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = safeFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  }
);

/* H E L P E R S */

const getFileType = (file: File) => {
  return /^image/.test(file.type)
    ? FileType.image
    : /^video/.test(file.type)
    ? FileType.video
    : /^audio/.test(file.type)
    ? FileType.audio
    : FileType.unknown;
};

// S L I C E

export const slice = createSlice({
  name: SLICE_ID,
  initialState,
  reducers: {
    setFileQueue: (state, action: PayloadAction<FileQueueData[]>) => {
      const payload: State['queueFiles'] = {};
      action.payload.forEach((data) => {
        payload[data.fileId] = data.positionInQueue;
      });
      state.queueFiles = payload;
    },
    clearQueuePosition: (state, action: PayloadAction<{ fileId: FileId }>) => {
      delete state.queueFiles[action.payload.fileId];
    },
    processingFileProgress: (state, action: PayloadAction<{ fileId: FileId; progress: number }>) => {
      const { fileId, progress } = action.payload;
      state.processingFiles[fileId] = progress;
    },
    setYoutubeProgress: (state, action: PayloadAction<{ fcId: FinalcutId; progress: number | null }>) => {
      const { fcId, progress } = action.payload;
      if (progress === null) delete state.youtubeFiles[fcId];
      else state.youtubeFiles[fcId] = Math.floor(progress);
    },
    uploadingFileProgress: (state, action: PayloadAction<UploadingFile>) => {
      const index = state.uploadingFiles.findIndex((el) => el.id === action.payload.id);
      if (index !== -1) state.uploadingFiles[index] = { ...action.payload };
      else state.uploadingFiles.push(action.payload);
    },
    uploadingFileEnd: (state, action: PayloadAction<string>) => {
      const newState = state.uploadingFiles.filter((el) => el.id !== action.payload);
      state.uploadingFiles = newState;
    },
    markRetryable: (state, action: PayloadAction<FileUplId>) => {
      state.retryableUploadIds[action.payload] = true;
    },
    clearRetryable: (state, action: PayloadAction<FileUplId>) => {
      delete state.retryableUploadIds[action.payload];
    },
    setFinalcutProgress: (state, action: PayloadAction<{ fcId: FinalcutId; progress: number }>) => {
      const { fcId, progress } = action.payload;
      if (progress === 100) delete state.finalcutDownloads[fcId];
      else state.finalcutDownloads[fcId] = Math.floor(progress);
    },
  },
});

export const { setFileQueue } = slice.actions;
export default slice.reducer;
